import React, { useMemo, useState } from 'react'
import { supportedCallSolutions } from '../../common/integrations'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useSessionAvailability } from '../ScopedBlock/hooks'
import { Col, Container, Row } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { ScopedBlock } from '../ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../ScopedBlock/models'
import { Tooltip } from '../Tooltip/Tooltip'
import { Button } from '../Button/Button'
import { MeetingProviderAccountIntegrationConfig } from '../../models/Account'
import { ReactComponent as LivePhoneIcon } from '../../assets/images/public-cloud-call-1.svg'
import { SelectPhoneMeetingModal } from '../../views/Upcoming/SelectPhoneMeetingModal'

export type SharedPhoneToolProps = {
  phoneConfig: MeetingProviderAccountIntegrationConfig
}

export const SharedPhoneTool: React.FC<SharedPhoneToolProps> = ({ phoneConfig }) => {
  const [showManageCallModal, setShowManageCallModal] = useState(false)
  const [doCall, setDoCall] = useState(false)
  const provider = useMemo(() => {
    return supportedCallSolutions.find((integration) => integration.id === phoneConfig.defaultProvider) ?? null
  }, [phoneConfig])
  const { tracked } = useTracking()
  const { available: sessionAvailable, isSuccess: sessionAvailabilitySuccess } = useSessionAvailability()

  const atRoomSessionLimitActions = () => {
    return sessionAvailabilitySuccess && sessionAvailable
  }

  const handleShowManageCallModal = tracked(
    ({ doCall }: { doCall: boolean }) => {
      setDoCall(doCall)
      setShowManageCallModal(true)
    },
    { eventDetails: { provider: { id: provider?.id ?? null, displayName: provider?.displayName ?? 'No Provider' } } },
    { eventNameExtractor: ({ doCall }) => (doCall ? TrackEvent.PHONE_START_CALL : TrackEvent.PHONE_ADD_TO_ONGOING_CALL) },
  )
  const handleHideManageCallModal = () => {
    setShowManageCallModal(false)
  }

  const ProviderIcon = provider?.icon

  const ProviderActionButtonsComponent = provider?.id === 'intulse' ? IntulseActionButtonsComponent : DefaultProviderActionButtonsComponent

  return (
    <Container className='d-flex flex-column gap-4 justify-content-between h-100 p-0'>
      {showManageCallModal && (
        <SelectPhoneMeetingModal show={showManageCallModal} phoneConfig={phoneConfig} doCall={doCall} onHide={handleHideManageCallModal} />
      )}
      <Row>
        <Col xs='auto' className='gx-3'>
          <div className='flex rounded integration-icon-wrapper'>
            <LivePhoneIcon width={'2rem'} height={'2rem'} className='me-1' />
          </div>
        </Col>
        <Col>
          <h5>Join a Live Phone Call</h5>
          <div className='paragraph-medium zocks-text-gray-600'>
            AI Assistant for shared lines and call solutions. If you have a dedicated phone number, you can also{' '}
            <Link className='button-link-medium zocks-text-primary cursor-pointer' to='/account/profile-settings/integrations'>
              link your phone
            </Link>{' '}
            to add Zocks to your Contacts permanently.
          </div>
        </Col>
      </Row>
      <Row>
        <ScopedBlock
          scope={{
            scope: 'room_session:start',
            superScope: ['room_session:admin', 'room:admin'],
          }}
          authActions={[atRoomSessionLimitActions]}
          missingScopeAction={MissingScopeAction.DISABLE}
        >
          <Container className='upcoming-integration-bottom-box gap-2'>
            {provider ? (
              <>
                {ProviderIcon && (
                  <Row className='align-items-center zocks-mb-0.75'>
                    <Col>
                      <div className='paragraph-small zocks-text-gray-600'>It works on</div>
                    </Col>
                    <Col xs='auto' className='flex align-items-center justify-content-end'>
                      <Tooltip title={provider.displayName} placement='top'>
                        <ProviderIcon width={'2rem'} height={'2rem'} />
                      </Tooltip>
                    </Col>
                  </Row>
                )}
                <ProviderActionButtonsComponent handleShowManageCallModal={handleShowManageCallModal} />
              </>
            ) : (
              <Row>
                <Col>
                  <Button variant='primary' className='w-100 px-0' onClick={() => handleShowManageCallModal({ doCall: false })}>
                    <i className='bi bi-telephone' />
                    Start Call
                  </Button>
                </Col>
              </Row>
            )}
          </Container>
        </ScopedBlock>
      </Row>
    </Container>
  )
}

interface ProviderActionButtonsComponentProps extends React.AllHTMLAttributes<HTMLElement> {
  handleShowManageCallModal: (details: { doCall: boolean }) => void
}

const DefaultProviderActionButtonsComponent: React.FC<ProviderActionButtonsComponentProps> = ({ handleShowManageCallModal, disabled, hidden }) => {
  return (
    <>
      <Row>
        <Col>
          <Button variant='primary' className='w-100 px-0' onClick={() => handleShowManageCallModal({ doCall: true })} disabled={disabled} hidden={hidden}>
            Start Call
          </Button>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button variant='primary' className='w-100 px-0' onClick={() => handleShowManageCallModal({ doCall: false })} disabled={disabled} hidden={hidden}>
            Add to ongoing call
          </Button>
        </Col>
      </Row>
    </>
  )
}

const IntulseActionButtonsComponent: React.FC<ProviderActionButtonsComponentProps> = ({ handleShowManageCallModal, disabled, hidden }) => {
  return (
    <>
      <Row>
        <Col>
          <Button variant='primary' className='w-100 px-0' onClick={() => handleShowManageCallModal({ doCall: false })} disabled={disabled} hidden={hidden}>
            Intulse Call
          </Button>
        </Col>
      </Row>
    </>
  )
}
